import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { getDb, isFirebaseChatEnabled } from '@services/firebase'
import { useChatStore } from '@store/chatStore'
import type { ChatMessageDto } from '@api/types/chat'

function mapDoc(
  doc: QueryDocumentSnapshot<DocumentData>,
  conversationId: string,
  currentUserId: string | undefined
): ChatMessageDto {
  const data = doc.data()
  const ts = data.timestamp?.toDate?.() ?? new Date()
  const senderDisplay = (data.senderDisplayName as string) ?? ''
  const isOwn = currentUserId != null && data.senderId === currentUserId
  // Ephemeral image limit is mirrored so recipients render the tap-to-view UI; the URL is withheld
  // in the mirror for limited images and fetched (counted) via the REST view endpoint on reveal.
  const imageMaxViews = (data.imageMaxViews as number | null | undefined) ?? null
  return {
    id: (data.id as string) ?? doc.id,
    conversationId: (data.conversationId as string) ?? conversationId,
    senderId: (data.senderId as string) ?? '',
    senderName: senderDisplay,
    senderDisplayName: senderDisplay,
    senderAvatarUrl: null,
    content: (data.content as string) ?? '',
    imageUrl: (data.imageUrl as string) ?? null,
    imageMaxViews,
    imageViewSeconds: (data.imageViewSeconds as number | null | undefined) ?? null,
    // Live-delivered limited image: default the recipient's allowance to the full cap (the real
    // per-viewer remaining comes from the view endpoint / a REST refresh). The sender sees their own.
    imageViewsRemaining: imageMaxViews != null && !isOwn ? imageMaxViews : undefined,
    timestamp: ts.toISOString(),
    isRead: false,
    isDeleted: Boolean(data.isDeleted),
    isEdited: false,
    isOwnMessage: isOwn,
    isSystemMessage: Boolean(data.isSystemMessage),
    editedAt: null,
    readBy: [],
    reactions: [],
  }
}

/**
 * Firestore-backed realtime receive for a single conversation — the SignalR ReceiveNewMessage /
 * ConversationUpdateSignal replacement. Subscribes to the conversation's latest messages and merges
 * them into the existing chatStore (dedup by id, so it never duplicates REST-loaded history).
 * No-op unless Firebase is configured (the VITE_FIREBASE_* web config is set).
 */
export function useFirestoreChat(conversationId: string | null, currentUserId: string | undefined) {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!isFirebaseChatEnabled || !conversationId) return
    const db = getDb()
    if (!db) return

    const q = query(
      collection(db, 'conversations', conversationId, 'messages'),
      orderBy('timestamp', 'desc'),
      limit(50)
    )

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const store = useChatStore.getState()
        const existing = new Set((store.messages[conversationId]?.items ?? []).map((m) => m.id))

        // Oldest-first so appends preserve chronological order.
        const changes = snapshot.docChanges().slice().reverse()
        let needsReferenceFetch = false
        for (const change of changes) {
          const msg = mapDoc(change.doc, conversationId, currentUserId)
          if (change.type === 'added') {
            if (!existing.has(msg.id)) {
              store.addMessage(conversationId, msg)
              existing.add(msg.id)
              // References are resolved per viewer, so the shared mirror carries only a flag.
              // A message that has them arrives looking empty — re-read it over REST, where the
              // server resolves them for this viewer specifically.
              if (change.doc.data().hasReferences) needsReferenceFetch = true
            }
          } else if (change.type === 'modified') {
            store.updateMessage(conversationId, msg.id, {
              content: msg.content,
              isDeleted: msg.isDeleted,
              imageUrl: msg.imageUrl,
            })
          }
        }

        if (needsReferenceFetch) {
          void queryClient.invalidateQueries({ queryKey: ['chatMessages', conversationId] })
        }
      },
      (err) => {
        console.warn('[firebase] chat snapshot error', err)
      }
    )

    return () => unsubscribe()
  }, [conversationId, currentUserId, queryClient])
}
